import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { QuestionCard } from './QuestionCard';
import { QuestionToolbar } from './QuestionToolbar';
import {
  refereeQuestionTableColumns,
  REFEREE_QUESTION_DEFAULT_SORT,
} from '../config/refereeQuestionTableColumns';
import type { RefereeQuestion } from '../types/referee-question.types';

interface QuestionListProps {
  questions: RefereeQuestion[];
  onEdit: (question: RefereeQuestion) => void;
  onDelete: (questionId: string) => void;
  isLoading?: boolean;
  error?: Error | null;
  hasData?: boolean;
}

function CenteredMessage({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode;
  tone?: 'muted' | 'destructive';
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-center py-12">
      <p className={tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground'}>
        {children}
      </p>
    </div>
  );
}

/** Cards, so sorting runs off a select rather than column headers. */
export function QuestionList({
  questions,
  onEdit,
  onDelete,
  isLoading,
  error,
  hasData,
}: QuestionListProps): React.JSX.Element {
  const { filteredSorted, sort, setSort, ...toolbar } = useTableFilters(
    questions,
    refereeQuestionTableColumns,
    REFEREE_QUESTION_DEFAULT_SORT
  );

  if (isLoading) return <CenteredMessage>Loading questions...</CenteredMessage>;
  if (error && !hasData) {
    return (
      <CenteredMessage tone="destructive">
        Failed to load questions: {error.message}
      </CenteredMessage>
    );
  }
  if (questions.length === 0) {
    return (
      <div className="space-y-3">
        <ResultCount count={0} />
        <CenteredMessage>No questions configured yet.</CenteredMessage>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ResultCount count={filteredSorted.length} />
      <QuestionToolbar toolbar={toolbar} sort={sort} onSortChange={setSort} />

      {filteredSorted.length === 0 && (
        <CenteredMessage>No questions match the filters.</CenteredMessage>
      )}

      {filteredSorted.map((question) => (
        <QuestionCard key={question.id} question={question} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </div>
  );
}
